import { useEffect, useRef, useState } from "react";
import {
  CalendarDays, Building2, Lock, UploadCloud, RefreshCw, Trash2, Eye, Send, Save,
  Pencil, Copy, ChevronLeft, ChevronRight, X, MapPin, Clock, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";
const labelClass = "mb-1.5 block text-xs font-semibold text-slate-600";

const STATUSES = [
  { key: "draft", label: "Brouillon", dot: "#2563EB", badge: "bg-slate-100 text-slate-500" },
  { key: "scheduled", label: "Programmé", dot: "#F59E0B", badge: "bg-amber-50 text-amber-600" },
  { key: "published", label: "Publié", dot: "#16A34A", badge: "bg-emerald-50 text-emerald-600" },
  { key: "finished", label: "Terminé", dot: "#94A3B8", badge: "bg-slate-100 text-slate-400" },
];
const statusOf = (s) => STATUSES.find((x) => x.key === (s || "published")) || STATUSES[2];

const toApi = (t) => (t ? t.replace(":", "h") : "");
const fromApi = (t) => (t ? t.replace("h", ":") : "");
const fileUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const fmtLongDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  const s = d.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const EMPTY_FORM = {
  title: "", category_key: "", free: true, price: "", date: "",
  time_start: "", time_end: "", capacity: "", reservation_required: false,
  recurring: false, description: "", image: "", status: "draft",
};

const PillPair = ({ value, onChange, testId }) => (
  <div className="flex gap-2">
    {[["Oui", true], ["Non", false]].map(([label, v]) => (
      <button
        key={label}
        type="button"
        data-testid={`${testId}-${label.toLowerCase()}`}
        onClick={() => onChange(v)}
        className={`flex-1 rounded-full border px-4 py-2 text-xs font-semibold transition ${
          value === v ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const EVENT_CATEGORIES = [
  { key: "soirees", label: "Soirée" },
  { key: "concerts", label: "Concert" },
  { key: "karaoke", label: "Karaoké" },
  { key: "humour", label: "Humour" },
  { key: "activites", label: "Activité" },
];

export const EventsView = ({ est }) => {
  const { t, lang } = useLanguage();
  const [events, setEvents] = useState(null);
  const categories = EVENT_CATEGORIES;
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [page, setPage] = useState(1);
  const fileInput = useRef(null);

  const PER_PAGE = 4;

  const load = () => api.get("/my/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) {
      toast.error(t("Formats acceptés : JPG, JPEG, PNG, WebP"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("Image trop lourde (max 10 Mo)"));
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro/uploads", fd);
      set("image", data.url);
      toast.success(t("Affiche téléversée — recadrage automatique appliqué à l'affichage"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const payload = (status) => ({
    title: form.title.trim(),
    venue: est.name,
    neighborhood: est.neighborhood || "Centre-Ville",
    category_key: form.category_key,
    date: form.date,
    time_label: toApi(form.time_start),
    time_end: toApi(form.time_end) || null,
    price: form.free ? 0 : parseFloat(String(form.price).replace(",", ".")) || 0,
    capacity: form.capacity ? parseInt(form.capacity, 10) : null,
    reservation_required: form.reservation_required,
    recurring: form.recurring,
    status,
    description: form.description.trim(),
    image: form.image || null,
  });

  const validate = () => {
    if (!form.title.trim() || !form.category_key || !form.date || !form.time_start) {
      toast.error(t("Remplis au moins le titre, la catégorie, la date et l'heure de début"));
      return false;
    }
    if (!form.free && !(parseFloat(String(form.price).replace(",", ".")) > 0)) {
      toast.error(t("Indique un prix pour un événement payant"));
      return false;
    }
    return true;
  };

  const save = async (status) => {
    if (!validate()) return;
    setBusy(status);
    try {
      const body = payload(status);
      if (editing) {
        await api.put(`/events/${editing}`, body);
        toast.success(t("Événement mis à jour"));
      } else {
        await api.post("/events", body);
        toast.success(status === "draft" ? "Brouillon enregistré" : status === "scheduled" ? "Événement programmé" : "Événement publié — visible dans GoSpot");
      }
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const startEdit = (ev) => {
    setEditing(ev.slug);
    setForm({
      title: ev.title,
      category_key: ev.category_key,
      free: !(ev.price > 0),
      price: ev.price > 0 ? String(ev.price) : "",
      date: ev.date,
      time_start: fromApi(ev.time_label),
      time_end: fromApi(ev.end_time || ""),
      capacity: ev.capacity ? String(ev.capacity) : "",
      reservation_required: !!ev.reservation_required,
      recurring: !!ev.recurring,
      description: ev.description || "",
      image: ev.image || "",
      status: ev.status || "published",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicate = async (ev) => {
    setBusy(`dup-${ev.slug}`);
    try {
      await api.post(`/pro/events/${ev.slug}/duplicate`);
      toast.success(`« ${ev.title} » dupliqué en brouillon`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Supprimer définitivement « ${ev.title} » et ses réservations ?`)) return;
    try {
      await api.delete(`/events/${ev.slug}`);
      toast.success(t("Événement supprimé"));
      if (editing === ev.slug) {
        setEditing(null);
        setForm(EMPTY_FORM);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const list = events || [];
  const totalPages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const pageEvents = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="mt-6 grid items-start gap-5 xl:grid-cols-2" data-testid="pro-events-view">
      {/* CRÉER UN ÉVÉNEMENT */}
      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6" data-testid="event-create-card">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <CalendarDays className="h-4 w-4 text-[#2563EB]" />
          {editing ? "Modifier l'événement" : "Créer un événement"}
        </h2>

        <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>{t("Établissement")}</label>
            <div className="relative">
              <input data-testid="event-establishment" value={est.name} disabled className={`${inputClass} bg-slate-50 pr-10 text-slate-500`} />
              <Lock className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
            </div>
          </div>
          <div>
            <label className={labelClass}>{t("Titre de l'événement")} <span className="text-red-500">*</span></label>
            <input data-testid="event-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("Ex. : Soirée disco au Dagobert")} className={inputClass} />
          </div>
        </div>

        <div className="mt-3.5 grid gap-3.5 sm:grid-cols-3">
          <div>
            <label className={labelClass}>{t("Catégorie")} <span className="text-red-500">*</span></label>
            <select data-testid="event-category" value={form.category_key} onChange={(e) => set("category_key", e.target.value)} className={`${inputClass} cursor-pointer`}>
              <option value="">{t("Choisir une catégorie")}</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t("Type d'événement")}</label>
            <div className="flex gap-2">
              {["Gratuit", "Payant"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`event-type-${t.toLowerCase()}`}
                  onClick={() => set("free", t === "Gratuit")}
                  className={`flex-1 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                    (t === "Gratuit") === form.free ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Prix {!form.free && <span className="text-red-500">*</span>}</label>
            <input
              data-testid="event-price"
              value={form.free ? "" : form.price}
              onChange={(e) => set("price", e.target.value)}
              disabled={form.free}
              placeholder="0,00 $"
              inputMode="decimal"
              className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-300`}
            />
          </div>
        </div>

        <div className="mt-3.5 grid gap-3.5 sm:grid-cols-4">
          <div>
            <label className={labelClass}>{t("Date")} <span className="text-red-500">*</span></label>
            <input data-testid="event-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t("Heure de début")} <span className="text-red-500">*</span></label>
            <input data-testid="event-time-start" type="time" value={form.time_start} onChange={(e) => set("time_start", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t("Heure de fin")}</label>
            <input data-testid="event-time-end" type="time" value={form.time_end} onChange={(e) => set("time_end", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t("Nombre de places")}</label>
            <input data-testid="event-capacity" type="number" min="1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder={t("Optionnel")} className={inputClass} />
          </div>
        </div>

        <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>{t("Réservation obligatoire")}</label>
            <PillPair testId="event-reservation" value={form.reservation_required} onChange={(v) => set("reservation_required", v)} />
          </div>
          <div>
            <label className={labelClass}>{t("Événement récurrent")}</label>
            <PillPair testId="event-recurring" value={form.recurring} onChange={(v) => set("recurring", v)} />
          </div>
        </div>

        <div className="mt-3.5">
          <label className={labelClass}>{t("Description")} <span className="text-red-500">*</span></label>
          <textarea
            data-testid="event-description"
            rows={4}
            maxLength={1000}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("Décris l'ambiance, la programmation, ce qui rend ton événement unique…")}
            className={`${inputClass} resize-none`}
          />
          <p className="mt-1 text-[10px] text-slate-400">{form.description.length} / 1000 caractères</p>
        </div>

        {/* PHOTO / AFFICHE */}
        <div className="mt-3.5">
          <label className={labelClass}>{t("Ajouter une photo / affiche")} <span className="text-red-500">*</span></label>
          <div className="flex flex-wrap items-start gap-3.5">
            <button
              type="button"
              data-testid="event-dropzone"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
              className={`flex h-36 w-full max-w-xs flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 text-center transition sm:w-64 ${
                dragOver ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 bg-[#F8FAFC] hover:border-blue-300"
              }`}
            >
              <UploadCloud className="h-7 w-7 text-[#2563EB]" />
              <span className="text-xs font-bold text-slate-700">{t("Glissez-déposez un fichier")}</span>
              <span className="text-[10px] text-slate-400">{t("ou téléversez depuis votre appareil")}</span>
              <span className="text-[9px] text-slate-400">{t("Formats acceptés : JPG, JPEG, PNG, WebP")}</span>
              <span className="text-[9px] text-slate-400">{t("Taille max. : 10 Mo")}</span>
              {busy === "upload" && <span className="text-[10px] font-semibold text-[#2563EB]">{t("Envoi en cours…")}</span>}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              data-testid="event-file-input"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
            />
            {form.image && (
              <div className="flex items-start gap-3">
                <div className="relative">
                  <img src={fileUrl(form.image)} alt={t("Affiche de l'événement")} data-testid="event-image-preview" className="h-36 w-40 rounded-2xl object-cover" />
                  <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">✓</span>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <button type="button" data-testid="event-image-replace" onClick={() => fileInput.current?.click()} className="flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] hover:underline">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Remplacer
                  </button>
                  <button type="button" data-testid="event-image-remove" onClick={() => set("image", "")} className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:underline">
                    <Trash2 className="h-3.5 w-3.5" />
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">{t("Recadrage automatique adapté à l'affichage de la page publique.")}</p>
        </div>

        {/* STATUT */}
        <div className="mt-4">
          <label className={labelClass}>{t("Statut de publication")}</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`event-status-${s.key}`}
                onClick={() => set("status", s.key)}
                className={`flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition ${
                  form.status === s.key ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                }`}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.dot }} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button type="button" data-testid="event-save-draft" onClick={() => save("draft")} disabled={!!busy} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-50">
            <Save className="h-3.5 w-3.5" />
            {busy === "draft" ? "Enregistrement…" : "Enregistrer le brouillon"}
          </button>
          <button type="button" data-testid="event-preview" onClick={() => setPreview(true)} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB]">
            <Eye className="h-3.5 w-3.5" />
            Aperçu
          </button>
          <button type="button" data-testid="event-publish" onClick={() => save(form.status === "scheduled" ? "scheduled" : "published")} disabled={!!busy} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-6 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-95 disabled:opacity-50">
            <Send className="h-3.5 w-3.5" />
            {busy === "published" || busy === "scheduled" ? "Publication…" : editing ? "Enregistrer les modifications" : "Publier maintenant"}
          </button>
          {editing && (
            <button type="button" data-testid="event-cancel-edit" onClick={() => { setEditing(null); setForm(EMPTY_FORM); }} className="rounded-full px-4 py-2.5 text-xs font-semibold text-slate-400 transition hover:text-slate-600">
              {t("Annuler")}
            </button>
          )}
        </div>
      </div>

      {/* COLONNE DROITE */}
      <div className="space-y-5">
        {/* ÉTABLISSEMENT LIÉ */}
        <div className="flex items-center gap-3 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="linked-establishment">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#EFF6FF]">
            <Building2 className="h-5 w-5 text-[#2563EB]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">
              Établissement lié : <span data-testid="linked-establishment-name">{est.name}</span>
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-400">
              {t("Connecté en tant que propriétaire. Vous gérez uniquement les événements de cet établissement.")}
            </p>
          </div>
          <Lock className="h-4 w-4 shrink-0 text-slate-300" />
        </div>

        {/* MES ÉVÉNEMENTS */}
        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6" data-testid="my-events-card">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <CalendarDays className="h-4 w-4 text-[#2563EB]" />
            Mes événements
          </h2>
          <p className="mt-1 text-xs text-slate-500">{t("Gérez vos événements existants. Seuls les événements de votre établissement sont affichés.")}</p>

          <div className="mt-4 space-y-3.5">
            {events === null ? (
              [0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-[#F8FAFC]" />)
            ) : list.length === 0 ? (
              <p data-testid="events-empty" className="rounded-2xl bg-[#F8FAFC] p-8 text-center text-xs text-slate-400">
                {t("Aucun événement pour le moment. Crée ton premier événement avec le formulaire ci-contre !")}
              </p>
            ) : (
              pageEvents.map((ev) => {
                const s = statusOf(ev.status);
                const count = ev.reservations_count || 0;
                return (
                  <div key={ev.slug} data-testid={`my-event-${ev.slug}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-100 p-3.5">
                    <img src={fileUrl(ev.image)} alt={ev.title} className="h-20 w-24 shrink-0 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-bold text-slate-900">{ev.title}</p>
                        <span data-testid={`my-event-status-${ev.slug}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${s.badge}`}>{s.label}</span>
                      </div>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                        <CalendarDays className="h-3 w-3 text-slate-300" />
                        {fmtLongDate(ev.date)}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                        <Clock className="h-3 w-3 text-slate-300" />
                        {ev.time_label}{ev.end_time ? ` – ${ev.end_time}` : ""}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                        <MapPin className="h-3 w-3 text-slate-300" />
                        {ev.category_label}
                      </p>
                    </div>
                    <div className="w-16 shrink-0 text-center">
                      <p className="text-xl font-extrabold text-slate-900" data-testid={`my-event-reservations-${ev.slug}`}>{count}</p>
                      <p className="text-[9px] font-medium text-slate-400">réservation{count > 1 ? "s" : ""}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" data-testid={`my-event-edit-${ev.slug}`} onClick={() => startEdit(ev)} className="flex flex-col items-center gap-0.5 rounded-xl px-2.5 py-1.5 text-slate-500 transition hover:bg-[#EFF6FF] hover:text-[#2563EB]">
                        <Pencil className="h-4 w-4" />
                        <span className="text-[9px] font-semibold">{t("Modifier")}</span>
                      </button>
                      <button type="button" data-testid={`my-event-duplicate-${ev.slug}`} onClick={() => duplicate(ev)} disabled={busy === `dup-${ev.slug}`} className="flex flex-col items-center gap-0.5 rounded-xl px-2.5 py-1.5 text-slate-500 transition hover:bg-[#EFF6FF] hover:text-[#2563EB] disabled:opacity-50">
                        <Copy className="h-4 w-4" />
                        <span className="text-[9px] font-semibold">{t("Dupliquer")}</span>
                      </button>
                      <button type="button" data-testid={`my-event-delete-${ev.slug}`} onClick={() => remove(ev)} className="flex flex-col items-center gap-0.5 rounded-xl px-2.5 py-1.5 text-red-400 transition hover:bg-red-50 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                        <span className="text-[9px] font-semibold">{t("Supprimer")}</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {list.length > 0 && (
            <div className="mt-5 flex items-center justify-between">
              <p className="text-[11px] text-slate-400" data-testid="events-pagination-label">
                Affichage de {Math.min(PER_PAGE, list.length - (page - 1) * PER_PAGE)} événement(s) sur {list.length}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1.5" data-testid="events-pagination">
                  <button type="button" data-testid="events-page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} aria-label={t("Page précédente")} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-blue-300 disabled:opacity-40">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`events-page-${i + 1}`}
                      onClick={() => setPage(i + 1)}
                      className={`h-8 w-8 rounded-lg text-xs font-bold transition ${page === i + 1 ? "bg-[#2563EB] text-white" : "border border-slate-200 text-slate-500 hover:border-blue-300"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button type="button" data-testid="events-page-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} aria-label={t("Page suivante")} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-blue-300 disabled:opacity-40">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MODALE APERÇU */}
      {preview && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={() => setPreview(false)} data-testid="event-preview-modal">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {form.image ? (
              <img src={fileUrl(form.image)} alt="" className="h-48 w-full object-cover" />
            ) : (
              <div className="flex h-48 items-center justify-center bg-[#F1F5F9] text-slate-300">
                <UploadCloud className="h-10 w-10" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${statusOf(form.status).badge}`}>{statusOf(form.status).label}</span>
                {form.category_key && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold text-slate-500">
                    {categories.find((c) => c.key === form.category_key)?.label}
                  </span>
                )}
              </div>
              <h3 className="mt-2 text-lg font-extrabold text-slate-900">{form.title || "Titre de l'événement"}</h3>
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                <CalendarDays className="h-3.5 w-3.5 text-slate-300" />
                {form.date ? fmtLongDate(form.date) : "Date à définir"}
                {form.time_start && ` • ${toApi(form.time_start)}${form.time_end ? ` – ${toApi(form.time_end)}` : ""}`}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                <MapPin className="h-3.5 w-3.5 text-slate-300" />
                {est.name} • {est.neighborhood}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-[#2563EB]">
                {form.free ? "Gratuit" : `${form.price || "0"} $`}
                {form.capacity && (
                  <span className="flex items-center gap-1 font-medium text-slate-400">
                    <Users className="h-3.5 w-3.5" />
                    {form.capacity} places
                  </span>
                )}
              </p>
              {form.description && <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-slate-600">{form.description}</p>}
              <button type="button" data-testid="event-preview-close" onClick={() => setPreview(false)} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0F172A] py-2.5 text-xs font-semibold text-white transition hover:bg-slate-700">
                <X className="h-3.5 w-3.5" />
                Fermer l'aperçu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
